/* eslint-disable no-console */

import { register } from 'register-service-worker'
import { queuePwaUpdateIfNoHandler } from '@/setup/pwaUpdateHandler'
import { STORAGE_KEYS } from '@/utils/storageKeys'

const disableFlag = String(import.meta.env.VITE_DISABLE_SW ?? '').toLowerCase() === 'true'
const DISABLE_SERVICE_WORKER = disableFlag

if (DISABLE_SERVICE_WORKER) {
  // Desregistrar qualquer SW antigo quando o modo debug estiver ativo
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(function(registrations) {
      for (const registration of registrations) {
        registration.unregister()
      }
    })
  }
}

const requestSWReload = (waitingWorker) => {
  if (!waitingWorker) {
    return
  }

  waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  waitingWorker.addEventListener('statechange', (event) => {
    if (event.target.state === 'activated') {
      window.location.reload()
    }
  })
}

const handleSWUpdate = (registration) => {
  if (!registration) {
    return
  }

  const waitingWorker = registration.waiting || registration.installing

  if (!waitingWorker) {
    return
  }

  const payload = {
    registration,
    refresh: () => requestSWReload(waitingWorker)
  }

  window.dispatchEvent(new CustomEvent('pwaUpdateAvailable', { detail: payload }))
  queuePwaUpdateIfNoHandler(payload)
}

document.addEventListener('swUpdated', (event) => {
  handleSWUpdate(event.detail)
})

if (!DISABLE_SERVICE_WORKER && import.meta.env.PROD) {
  register(`${import.meta.env.BASE_URL}sw.js`, {
    ready() {},
    registered(registration) {
      // Verificar por atualizações a cada 1 hora
      setInterval(() => {
        registration.update()
      }, 1000 * 60 * 60)
    },
    cached() {},
    updatefound() {},
    updated(registration) {
      // Disparar evento customizado para o app mostrar notificação de atualização
      document.dispatchEvent(
        new CustomEvent('swUpdated', { detail: registration })
      )
    },
    offline() {},
    error(error) {
      console.error('❌ Erro durante o registro do service worker:', error)
    }
  })
}

// Registrar PWA install prompt
const PWA_INSTALLED_STORAGE_KEY = STORAGE_KEYS.PWA_INSTALLED
let deferredPrompt = null

const isDisplayModeInstalled = () => {
  const modes = ['standalone', 'minimal-ui', 'fullscreen']
  return modes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches)
}

/** App aberto pelo ícone / já instalado (inclui visita no Chrome após instalar) */
export const markPWAAsInstalled = () => {
  try {
    localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, '1')
  } catch {
    /* storage bloqueado */
  }
  deferredPrompt = null
  window.deferredPrompt = null
}

export const isPWAInstalled = () => {
  if (isDisplayModeInstalled()) {
    return true
  }

  if (window.navigator.standalone === true) {
    return true
  }

  if (document.referrer.includes('android-app://')) {
    return true
  }

  try {
    if (localStorage.getItem(PWA_INSTALLED_STORAGE_KEY) === '1') {
      return true
    }
  } catch {
    /* ignore */
  }

  return false
}

/** Chrome/Android: detecta PWA instalado mesmo na aba do navegador */
export async function detectPWAInstalledAsync() {
  if (isPWAInstalled()) {
    return true
  }

  if (typeof navigator !== 'undefined' && typeof navigator.getInstalledRelatedApps === 'function') {
    try {
      const related = await navigator.getInstalledRelatedApps()
      if (related?.length > 0) {
        markPWAAsInstalled()
        return true
      }
    } catch (err) {
      console.warn('[PWA] getInstalledRelatedApps falhou:', err)
    }
  }

  return false
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()

  if (isPWAInstalled()) {
    return
  }

  void detectPWAInstalledAsync().then((alreadyInstalled) => {
    if (alreadyInstalled) {
      return
    }

    deferredPrompt = e
    window.deferredPrompt = e
    window.dispatchEvent(new CustomEvent('pwaInstallAvailable'))
  })
})

window.addEventListener('appinstalled', () => {
  markPWAAsInstalled()
  window.dispatchEvent(new CustomEvent('pwaInstalled'))
})

// Exportar função para mostrar prompt de instalação
export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    console.warn('⚠️ Prompt de instalação não está disponível')
    return false
  }

  // Mostrar o prompt
  deferredPrompt.prompt()

  // Aguardar resposta do usuário
  const { outcome } = await deferredPrompt.userChoice

  deferredPrompt = null
  window.deferredPrompt = null

  if (outcome === 'accepted') {
    markPWAAsInstalled()
  }

  return outcome === 'accepted'
}

detectPWAInstalledAsync()
